import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { DynamicContentModalComponent } from '../../../Views/Work/Accounts/proprietary/dynamic-content-outlet/dynamic-content-modal.component';
import { AppService } from './app.service';
import { DateFormulaType } from './date-formula.types';

@Injectable({
    providedIn: 'root'
})
export class DateFormulaService {
    private callbackFunction: any;
    private modalRef: NgbModalRef;

    constructor(private modalService: NgbModal, private app: AppService) { }

    public openDialog(formulaType: DateFormulaType, callbackFunction: any) {
        if (callbackFunction) {
            this.callbackFunction = callbackFunction;
        }
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            backdrop: 'static',
            windowClass: 'ep-modal-edit-panel'
        });

        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = 'DateFormulaDialogComponent';
        this.modalRef.componentInstance.dynamicData = { modal: true, formulaType: formulaType };
    }

    closeDialog(result) {
        if (this.modalRef) {
            this.modalRef.close();
        }
        if (this.callbackFunction) {
            this.callbackFunction(result);
            this.callbackFunction = null;
        }
    }

    validateFormula(formulaType: DateFormulaType, formula: string): boolean {
        const termsCodes = [
            'trandate',
            'effdate',
            'eomtrandate',
            'eomeffdate',
            '>traneff',
            'eom>traneff',
            'eom',
            'bom',
            'boy',
            'eoy',
            'fboy',
            'feoy',
            'eom>traneffplusonemonth',
            'eoqtrandate',
            'eoqeffdate',
            'eoq>traneff'
        ];

        const dateCodes = ['today', 'bom', 'eom', 'boy', 'eoy', 'bow', 'eow', 'fboy', 'feoy'];

        let valid: boolean;
        if (formula) {
            let parsedFormula = this.parseDateFormula(formula);

            switch (parsedFormula.length) {
                case 1:
                    if (
                        (formulaType == DateFormulaType.Terms && termsCodes.includes(parsedFormula[0].toLowerCase())) ||
                        (formulaType == DateFormulaType.Date && dateCodes.includes(parsedFormula[0].toLowerCase()))
                    ) {
                        valid = true;
                    } else {
                        valid = false;
                    }
                    break;

                case 4:
                    if (
                        (formulaType == DateFormulaType.Terms && termsCodes.includes(parsedFormula[0].toLowerCase())) ||
                        (formulaType == DateFormulaType.Date && dateCodes.includes(parsedFormula[0].toLowerCase()))
                    ) {
                        if (['+', '-'].includes(parsedFormula[1])) {
                            if (!Number.isNaN(parseInt(parsedFormula[2], 10))) {
                                if (['day', 'days', 'month', 'months', 'year', 'years'].includes(parsedFormula[3].toLowerCase())) {
                                    valid = true;
                                } else {
                                    valid = false;
                                }
                            } else {
                                valid = false;
                            }
                        } else {
                            valid = false;
                        }
                    } else {
                        valid = false;
                    }
                    break;

                default:
                    valid = false;
            }
        } else {
            valid = true;
        }

        return valid;
    }

    parseDateFormula(dateFormula: string): any[] {
        let parsedFormula = dateFormula.split(' ');

        for (var idx = parsedFormula.length - 1; idx >= 0; idx--) {
            if (parsedFormula[idx] == '') {
                parsedFormula.splice(idx, 1);
            }
        }

        // if date formula does not provide a value after the add/subtract integer segment assume "days"
        if (parsedFormula.length == 3) {
            if (['+', '-'].includes(parsedFormula[1])) {
                if (!Number.isNaN(parseInt(parsedFormula[2], 10))) {
                    parsedFormula.push('days');
                }
            }
        }

        return parsedFormula;
    }

    getBeginCodes(formulaType: DateFormulaType): [any] {
        let codes: any = [];
        let tempCodes: any = [];
        if (formulaType == DateFormulaType.Terms) {
            tempCodes.push('TranDate');
            tempCodes.push('EffDate');
            tempCodes.push('eomTranDate');
            tempCodes.push('eomEffDate');
            tempCodes.push('>TranEff');
            tempCodes.push('eom>TranEff');
            tempCodes.push('eoyTranDate');
            tempCodes.push('eoyEffDate');
            tempCodes.push('bom');
            tempCodes.push('eom');
            tempCodes.push('boy');
            tempCodes.push('eoy');
            tempCodes.push('fboy');
            tempCodes.push('feoy');
            tempCodes.push('eom>traneffplusonemonth');
            tempCodes.push('eoqTranDate');
            tempCodes.push('eoqEffDate');
            tempCodes.push('eoq>TranEff');
        } else {
            tempCodes.push('today');
            tempCodes.push('bom');
            tempCodes.push('eom');
            tempCodes.push('boy');
            tempCodes.push('eoy');
            tempCodes.push('bow');
            tempCodes.push('eow');
            tempCodes.push('fboy');
            tempCodes.push('feoy');
        }

        for (var idx in tempCodes) {
            codes.push({ Code: tempCodes[idx], Code_Desc: tempCodes[idx] });
        }

        return codes;
    }

    calcDate(formula: string): string {
        let value = '';

        let today: moment.Moment = moment();
        let date: moment.Moment;

        let parsedformula = this.parseDateFormula(formula);

        if (parsedformula.length == 1 || parsedformula.length == 4) {
            switch (parsedformula[0].toLowerCase()) {
                case 'today':
                    date = today.clone();
                    break;
                case 'bom':
                    date = today.clone().startOf('month');
                    break;
                case 'eom':
                    date = today.clone().endOf('month');
                    break;
                case 'bow':
                    date = today.clone().startOf('week');
                    break;
                case 'eow':
                    date = today.clone().endOf('week');
                    break;
                case 'boy':
                    date = today.clone().startOf('year');
                    break;
                case 'eoy':
                    date = today.clone().endOf('year');
                    break;
                case 'fboy':
                    let currFiscalStart = this.app.getBookOptionValue('Curr_Fiscal_Begin_Date');
                    date = moment(currFiscalStart).clone();
                    break;
                case 'feoy':
                    let currFiscalEnd = this.app.getBookOptionValue('Curr_Fiscal_End_Date');
                    date = moment(currFiscalEnd).clone();
                    break;

                default:
                    date = moment(parsedformula[0]);
                    if (date.format('MM/DD/YYYY') == '01/01/1900') {
                        date = today;
                    }
                    break;
            }

            if (parsedformula.length == 4) {
                switch (parsedformula[1]) {
                    case '+':
                        date.add(parsedformula[2], parsedformula[3]);

                        switch (parsedformula[0]) {
                            case 'eom':
                                date.endOf('month');
                        }

                        break;

                    case '-':
                        date.subtract(parsedformula[2], parsedformula[3]);

                        switch (parsedformula[0]) {
                            case 'eom':
                                date.endOf('month');
                        }

                        break;
                }
            }

            value = date.format('MM/DD/YYYY');
        } else {
            value = 'Invalid date formula entered!';
        }

        return value;
    }

    calcTermDate(formula: string, tranDateString: string, effDateString: string): string {
        let value = '';
        let pos = -1;

        pos = tranDateString.indexOf('T');
        if (pos > -1) {
            tranDateString = tranDateString.substring(0, pos);
        }

        pos = effDateString.indexOf('T');
        if (pos > -1) {
            effDateString = effDateString.substring(0, pos);
        }

        let today: moment.Moment = moment();
        let tranDate: moment.Moment = moment(tranDateString);
        let effDate: moment.Moment = moment(effDateString);
        let date: moment.Moment;

        let parsedformula = this.parseDateFormula(formula);

        if (parsedformula.length == 1 || parsedformula.length == 4) {
            switch (parsedformula[0].toLowerCase()) {
                case 'bom':
                    date = today.clone().startOf('month');
                    break;
                case 'eom':
                    date = today.clone().endOf('month');
                    break;
                case 'boy':
                    date = today.clone().startOf('year');
                    break;
                case 'eoy':
                    date = today.clone().endOf('year');
                    break;
                case 'fboy':
                    let currFiscalStart = this.app.getBookOptionValue('Curr_Fiscal_Begin_Date');
                    date = moment(currFiscalStart).clone();
                    break;
                case 'feoy':
                    let currFiscalEnd = this.app.getBookOptionValue('Curr_Fiscal_End_Date');
                    date = moment(currFiscalEnd).clone();
                    break;
                case 'trandate':
                    date = tranDate.clone();
                    break;

                case 'effdate':
                    date = effDate.clone();
                    break;

                case '>traneff':
                    if (tranDate.isAfter(effDate)) {
                        date = tranDate.clone();
                    } else {
                        date = effDate.clone();
                    }
                    break;

                case 'eomtrandate':
                    date = tranDate.clone().endOf('month');
                    break;

                case 'eomeffdate':
                    date = effDate.clone().endOf('month');
                    break;

                case 'eom>traneff':
                    if (tranDate.isAfter(effDate)) {
                        date = tranDate.clone().endOf('month');
                    } else {
                        date = effDate.clone().endOf('month');
                    }
                    break;

                case 'eoqtrandate':
                    date = tranDate.clone().endOf('quarter');
                    break;
                case 'eoqeffdate':
                    date = effDate.clone().endOf('quarter');
                    break;
                case 'eoq>traneff':
                    if (tranDate.isAfter(effDate)) {
                        date = tranDate.clone().endOf('quarter');
                    } else {
                        date = effDate.clone().endOf('quarter');
                    }
                    break;
                case 'eoytrandate':
                    date = tranDate.clone().endOf('year');
                    break;
                case 'eoyeffdate':
                    date = effDate.clone().endOf('year');
                    break;
                case 'eom>traneffplusonemonth':
                    if (tranDate.isAfter(effDate)) {
                        date = tranDate.clone().add(1, 'month').endOf('month');
                    } else {
                        date = effDate.clone().add(1, 'month').endOf('month');
                    }
                    break;
            }

            if (parsedformula.length == 4) {
                switch (parsedformula[1]) {
                    case '+':
                        date.add(parsedformula[2], parsedformula[3]);

                        switch (parsedformula[0]) {
                            case 'eom':
                                date.endOf('month');
                        }

                        break;

                    case '-':
                        date.subtract(parsedformula[2], parsedformula[3]);

                        switch (parsedformula[0]) {
                            case 'eom':
                                date.endOf('month');
                        }

                        break;
                }
            }

            value = date.format('MM/DD/YYYY');
        } else {
            value = 'Invalid date formula entered!';
        }

        return value;
    }
}
